/* © CNES */
import { Divider } from 'primereact/divider';
import { ScrollPanel } from 'primereact/scrollpanel';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { keycloakAdapter } from '../../../../api/authentication/AuthenticateAPI';
import { useGetItems } from '../../../../api/items/UseItemsQuery';
import { allVariableOptions } from '../../../../assets/config/filters/PostelReferenciel';
import {
  filterSections,
  Missions,
  refMissions,
} from '../../../../assets/config/filters/Referenciel';
import { ConfigContext } from '../../../../context/ConfigContext';
import { FilterDataContext } from '../../../../context/FilterDataContext';
import { CollectionsForUser } from '../../../../models/CollectionsForUser';
import { subMissionInitValues } from '../../../../models/constants/Filter';
import { KeycloakUserInfo } from '../../../../models/KeycloakUserInfo';
import { toMyKeycloakProfile } from '../../../../models/MyInformation';
import { SelectButtonItem } from '../../../../models/SelectButtonItem';
import {
  CLOUD_COVER,
  DATASET,
  INSTRUMENT,
  NODATA_PIXEL,
  PPARAMETER,
  VISIBILITY,
  WATER_COVER,
} from '../../../shared/Constants';
import useUrlUpdater from '../../../shared/hooks/useUrlUpdater';
import {
  filterSubSectionsOfMission,
  getDefaultSelectedOption,
  getOptionsForSubSections,
  parseFilterContextToUrl,
  parseUrlToContextFilter,
  showCloudCover,
  transformFilterData,
} from '../../../shared/Utils';
import { AcquisitionTypeCondition } from './acquisitionTypeCondition/AcquisitionTypeCondition';
import { DataProductCondition } from './dataProductCondition/DataProductCondition';
import { FilterDynamicList } from './FilterDynamicList/FilterDynamicList';
import { FilterManageButtons } from './FilterManageButtons/FilterManageButtons';
import './Filters.scss';
import { GenericSlider } from './GenericSlider/GenericSlider';
import { MissionCondition } from './missionCondition/MissionCondition';
import { ModeCondition } from './modeCondition/ModeCondition';
import { PlatformCondition } from './platformCondition/PlatformCondition';
import { ProductTypeCondition } from './productTypeCondition/ProductTypeCondition';
import { SensorModeCondition } from './sensorMode/SensorModeCondition';
import { VariableCondition } from './variableCondition/VariableCondition';
import { WhenCondition } from './WhenCondition/WhenCondition';
import { WhereCondition } from './whereCondition/WhereCondition';

/**
 * Filters Props.
 */
export interface FiltersProps {
  setIsFetching: (value: boolean) => void;
  setFiltersVisibility: Function;
  userCollections: CollectionsForUser;
}

/**
 * SliderValues Props.
 */
export interface SliderValuesProps {
  isInitialisation: boolean;
  sliderTable: { values: number[]; param: string }[];
}
/**
 * SelectedProductType
 */
type SelectedProductType = string | number[] | null | undefined;

/**
 * Filters displays the full filter options to filter regards items from.
 * @returns Filters Element.
 */
export const Filters = ({
  setIsFetching,
  setFiltersVisibility,
  userCollections,
}: FiltersProps) => {
  const context = useContext(ConfigContext);
  const { setFilterData, filterData, selectedMissions, setSelectedMissions } = useContext(FilterDataContext);
  const [selectedPostelProductType, setSelectedPostelProductType] =
    useState<SelectedProductType>(
      getDefaultSelectedOption(filterData[PPARAMETER], allVariableOptions)
    );
  const [dataProductOptions, setDataProductOptions] = useState<
    SelectButtonItem[] | null
  >(null);
  const [platformOptions, setPlatformOptions] = useState<
    SelectButtonItem[] | null
  >(null);
  const [sensorModeOptions, setSensorModeOptions] = useState<
    SelectButtonItem[] | null
  >(null);
  const [variableOptions, setVariableOptions] = useState<
    SelectButtonItem[] | null
  >(null);
  const [visibilityOptions, setVisibilityOptions] = useState<
    SelectButtonItem[] | null
  >(null);
  const [acquisitionTypeOptions, setAcquisitionTypeOptions] = useState<
    SelectButtonItem[] | null
  >(null);
  const [isCloudCoverVisible, setIsCloudCoverVisible] =
    useState<boolean>(false);
  const [isPostelSectionsVisible, setIsPostelSectionsVisible] =
    useState<boolean>(false);
  const [areTrishnaSectionsVisible, setAreTrishnaSectionsVisible] =
    useState<boolean>(false);
  const [areCO3DSectionVisible, setAreCO3DSectionVisible] =
    useState<boolean>(false);
  const [sliderValues, setSliderValues] = useState<SliderValuesProps>({
    isInitialisation: true,
    sliderTable: [
      {
        values: [0, 100],
        param: filterSections.cloudCover.param,
      },
      {
        values: [0, 100],
        param: filterSections.waterCover.param,
      },
      {
        values: [0, 100],
        param: filterSections.noDataPixel.param,
      },
    ],
  });
  const [isUserAllowedToTrishnaMode, setIsUserAllowedToTrishnaMode] =
    useState<boolean>(false);
  const [isUserAllowedToCO3DMode, setIsUserAllowedToCO3DMode] =
    useState<boolean>(false);

  /**
   * Initialize selectedMissions from filterData[DATASET] if not already set.
   */
  useEffect(() => {
    if (!selectedMissions && filterData[DATASET]) {
      const missions = getDefaultSelectedOption(filterData[DATASET], refMissions);
      setSelectedMissions(missions);
    }
  }, [filterData, selectedMissions, setSelectedMissions]);

  /**
   * Initializes the slider values based on the filterData.
   */
  useEffect(() => {
    // Only initialize if sliderValues is empty and filterData is available
    if (
      sliderValues.isInitialisation &&
      (filterData[CLOUD_COVER] !== null ||
        filterData[WATER_COVER] !== null ||
        filterData[NODATA_PIXEL] !== null)
    ) {
      setSliderValues({
        isInitialisation: false,
        sliderTable: [
          {
            values: (filterData[CLOUD_COVER] as unknown as number[]) || [
              0, 100,
            ],
            param: filterSections.cloudCover.param,
          },
          {
            values: (filterData[WATER_COVER] as unknown as number[]) || [
              0, 100,
            ],
            param: filterSections.waterCover.param,
          },
          {
            values: (filterData[NODATA_PIXEL] as unknown as number[]) || [
              0, 100,
            ],
            param: filterSections.noDataPixel.param,
          },
        ],
      });
    } else if (
      !sliderValues.isInitialisation &&
      filterData[CLOUD_COVER] === null &&
      filterData[WATER_COVER] === null &&
      filterData[NODATA_PIXEL] === null
    ) {
      // If values are reset, initialize with default values
      setSliderValues({
        isInitialisation: true,
        sliderTable: [
          { values: [0, 100], param: filterSections.cloudCover.param },
          { values: [0, 100], param: filterSections.waterCover.param },
          { values: [0, 100], param: filterSections.noDataPixel.param },
        ],
      });
    }
    // eslint-disable-next-line
  }, [sliderValues, filterData]);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isFetching, data } = useGetItems(
    {
      params: filterData,
      page: context.queryPage,
      intersects: context.searchZone.value,
      bbox:
        context.drawnShape ??
        (context.mapExtent.currentView ? context.mapExtent.extent : undefined),
      selectedMissions: Array.isArray(selectedMissions) ? selectedMissions : null,
    },
    navigate,
    context.setError400
  );

  /**
   * Get params from URL on component first render
   */
  useEffect(() => {
    setFiltersVisibility(true);
    const params: { key: string; value: string }[] = [];
    searchParams.forEach((value, key) => {
      params.push({ key, value });
    });
    if (params.length) {
      const {
        contextFilter,
        selectedMissions,
        selectedPostelProductType,
        drawnShape,
        searchZone,
      } = parseUrlToContextFilter(params);

      setSelectedMissions(selectedMissions);
      setSelectedPostelProductType(selectedPostelProductType);

      setFilterData({
        ...contextFilter,
        ...(selectedMissions?.includes(Missions.GEOV2_AVHRR) && {
          [INSTRUMENT]: ['AVHRR'],
        }),
      });
      if (drawnShape) {
        setTimeout(() => {
          context.setDrawnShape(drawnShape);
        }, 0);
      } else if (searchZone) {
        setTimeout(() => {
          context.setSearchZone(searchZone);
        }, 0);
      }
    }
    // eslint-disable-next-line
  }, []);

  /**
   * setIsFetching when isFetching changes.
   */
  useEffect(() => {
    setIsFetching(isFetching);
  }, [isFetching, setIsFetching]);

  /**
   * set searchedFeatures and featuresCount when data changes.
   */
  useEffect(() => {
    if (filterData[DATASET] && data) {
      context.setSearchedFeatures(data?.features, data?.context?.matched || 0);
    } else {
      context.setSearchedFeatures([], data?.context?.matched || 0);
    }
    // eslint-disable-next-line
  }, [data, filterData[DATASET]]);

  /**
   * filter values of platform, product type and sensor mode options on mission values change
   * if all mission values are selected then set all options
   */
  useEffect(() => {
    const {
      filteredPlatformOptions,
      filteredProductTypesOptions,
      filteredSensorModeOptions,
      filteredVisibilityOptions,
      filteredAcquisitionTypeOptions,
    } = getOptionsForSubSections(selectedMissions);

    handleShowHidePostelSections(selectedMissions);
    setPlatformOptions(filteredPlatformOptions);
    setDataProductOptions(filteredProductTypesOptions);
    setSensorModeOptions(filteredSensorModeOptions);
    setVisibilityOptions(filteredVisibilityOptions);
    setAcquisitionTypeOptions(filteredAcquisitionTypeOptions);
    // eslint-disable-next-line
  }, [selectedMissions]);

  /**
   * set cloud cover Visibility based on selected missions and filter data.
   */
  useEffect(() => {
    if (showCloudCover(selectedMissions, filterData)) {
      setIsCloudCoverVisible(true);
    } else {
      setIsCloudCoverVisible(false);
    }
  }, [selectedMissions, filterData]);

  /**
   * set visibility of Trishna sections based on selected missions.
   */
  useEffect(() => {
    if (selectedMissions?.includes(Missions.trishna as never)) {
      setAreTrishnaSectionsVisible(true);
    } else {
      setAreTrishnaSectionsVisible(false);
    }
  }, [selectedMissions]);

  /**
   * set visibility of CO3D sections based on selected missions.
   */
  useEffect(() => {
    if (selectedMissions?.includes(Missions.CO3D as never)) {
      setAreCO3DSectionVisible(true);
    } else {
      setAreCO3DSectionVisible(false);
    }
  }, [selectedMissions]);

  /**
   *  Update options for Variable section on selected product type changed
   */
  useEffect(() => {
    let filteredVariableOptions: SelectButtonItem[] | null =
      selectedPostelProductType === 'All' ? allVariableOptions : null;
    if (Array.isArray(selectedPostelProductType)) {
      filteredVariableOptions = allVariableOptions.filter((variableItem) => {
        if (Array.isArray(variableItem.productTypeId)) {
          return variableItem.productTypeId.some((id) =>
            selectedPostelProductType.includes(id)
          );
        } else {
          return selectedPostelProductType.includes(variableItem.productTypeId);
        }
      });
    }
    setVariableOptions(filteredVariableOptions);
  }, [selectedPostelProductType]);

  /**
   * Check if the user is allowed to access the mode section based on their profile.
   * Logic:
   * - For TRISHNA:
   *   - TRISHNA_TEST only: No mode section visible (backend filters to PUBLIC products automatically)
   *   - TRISHNA_EXPERT (with or without TRISHNA_TEST): Mode section visible with PUBLIC and EXPERT options
   * - For CO3D:
   *   - CO3D access: Mode section visible with PUBLIC and PRIVATE options
   */
  useEffect(() => {
    // S'assurer que l'état est initialisé à false par défaut
    if (!context.userLoggedIn.email) {
      setIsUserAllowedToTrishnaMode(false);
      setIsUserAllowedToCO3DMode(false);
      return;
    }

    // En mode test, ne pas exécuter la vérification Keycloak
    if (process.env.NODE_ENV !== 'production') {
      setIsUserAllowedToTrishnaMode(false);
      setIsUserAllowedToCO3DMode(false);
      return;
    }

    // En production avec un utilisateur connecté, vérifier les droits
    const checkUserAccess = async () => {
      try {
        // Charger le profil utilisateur
        const userProfile = keycloakAdapter.loadUserProfile();
        const userInfo = keycloakAdapter.loadUserInfo();

        const [profile, userInfoResult] = await Promise.all([
          userProfile,
          userInfo,
        ]);

        // Vérifier d'abord si l'utilisateur provient de sources interdites
        const myProfile = toMyKeycloakProfile(profile);
        const sources = ['theia', 'aeris', 'gaiadata'];
        if (
          myProfile &&
          sources.includes(myProfile.attributes?.auth_source[0] as string)
        ) {
          setIsUserAllowedToTrishnaMode(false);
          setIsUserAllowedToCO3DMode(false);
          return;
        }

        // Vérifier les groupes d'accès
        const accessGroups =
          (userInfoResult as KeycloakUserInfo).accessGroups || [];

        // Vérifier les droits pour TRISHNA
        // La section mode TRISHNA est visible seulement si l'utilisateur a TRISHNA_EXPERT
        // TRISHNA_TEST seul ne donne PAS accès à la section mode
        const hasTrishnaExpert = accessGroups.includes('TRISHNA_EXPERT');
        setIsUserAllowedToTrishnaMode(hasTrishnaExpert);

        // Vérifier les droits pour CO3D
        // La section mode CO3D est visible si l'utilisateur a CO3D
        const hasCO3D = accessGroups.includes('CO3D');
        setIsUserAllowedToCO3DMode(hasCO3D);
      } catch (error) {
        console.error('Error checking user access:', error);
        setIsUserAllowedToTrishnaMode(false);
        setIsUserAllowedToCO3DMode(false);
      }
    };

    checkUserAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.userLoggedIn.email]);

  /**
   * Initialize VISIBILITY filter to empty array when Mode section becomes visible
   * This allows distinguishing between:
   * - User without expert rights (VISIBILITY stays null → force to 'public')
   * - User with expert rights but nothing selected (VISIBILITY = [] → see all products)
   */
  useEffect(() => {
    const shouldShowModeSection =
      (areTrishnaSectionsVisible && isUserAllowedToTrishnaMode) ||
      (areCO3DSectionVisible && isUserAllowedToCO3DMode);

    if (shouldShowModeSection && filterData[VISIBILITY] === null) {
      // Initialize to empty array when Mode section is visible
      setFilterData({ ...filterData, [VISIBILITY]: [] });
    } else if (!shouldShowModeSection && filterData[VISIBILITY] !== null) {
      // Reset to null when Mode section is hidden
      setFilterData({ ...filterData, [VISIBILITY]: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    areTrishnaSectionsVisible,
    isUserAllowedToTrishnaMode,
    areCO3DSectionVisible,
    isUserAllowedToCO3DMode,
  ]);

  /**
   * show or hide sections when collection postel selected
   * @param missions list of missions values
   * @returns
   */
  const handleShowHidePostelSections = useCallback(
    (missions: string | number[] | null | undefined) => {
      if (
        missions instanceof Array &&
        (missions.includes(Missions.postel) ||
          missions.includes(Missions.GEOV2_AVHRR))
      ) {
        setIsPostelSectionsVisible(true);
      } else {
        setIsPostelSectionsVisible(false);
      }
      setVariableOptions(null);
    },
    []
  );

  // Updates the URL parameters based on changes in the map extent, drawn shapes, or filter data
  useUrlUpdater();

  /**
   * handleChange on Condition components.
   */
  const handleChange = useCallback(
    (value: string | number[], key: string) => {
      context.setQueryPage(1);
      let newFilterValue = null;
      if (key === filterSections.mission.param) {
        setSelectedMissions(Array.isArray(value) ? value : null);
        if (value?.length > 0) {
          newFilterValue = {
            ...filterSubSectionsOfMission(value, filterData),
            ...transformFilterData(key, value, filterData),
          };
          if (
            newFilterValue[CLOUD_COVER] === null &&
            newFilterValue[WATER_COVER] === null &&
            newFilterValue[NODATA_PIXEL] === null
          ) {
            // If values are reset, initialize with default values.
            setSliderValues({ ...sliderValues, isInitialisation: false });
          } else {
            // If some values are set, keep values from context.
            setSliderValues({ ...sliderValues, isInitialisation: true });
          }
        } else {
          newFilterValue = {
            ...filterSubSectionsOfMission(value, filterData),
            ...subMissionInitValues,
          };
        }
      } else if (key === filterSections.productType.param) {
        setSelectedPostelProductType(value);
        newFilterValue = transformFilterData(key, value, filterData);
      } else {
        newFilterValue = transformFilterData(key, value, filterData);
      }
      setFilterData(newFilterValue);
      const urlParams = parseFilterContextToUrl(
        { ...filterData, ...newFilterValue },
        context.drawnShape,
        context.mapExtent.currentView ? context.mapExtent.extent : undefined,
        context.searchZone
      );

      navigate({
        pathname: urlParams ? '/search' : '/',
        ...(urlParams && { search: urlParams }),
      });
    },
    // eslint-disable-next-line
    [setFilterData, navigate, context.mapExtent, filterData, context.drawnShape]
  );

  return (
    <ScrollPanel
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <div className="filterTab">
        <FilterManageButtons setSelectedMissions={setSelectedMissions} />
        <FilterDynamicList setSelectedMissions={setSelectedMissions} />
        <Divider />
        <WhenCondition handleChange={handleChange} />
        <WhereCondition handleChange={handleChange} />
        <MissionCondition
          handleChange={handleChange}
          userCollections={userCollections}
        />
        {isPostelSectionsVisible ? (
          <>
            <ProductTypeCondition handleChange={handleChange} />
            <VariableCondition
              handleChange={handleChange}
              variableOptions={variableOptions}
            />
          </>
        ) : (
          <div>
            {platformOptions?.length !== 0 &&
              platformOptions?.length !== undefined && (
                <PlatformCondition
                  handleChange={handleChange}
                  platformOptions={platformOptions}
                />
              )}
            {dataProductOptions?.length !== 0 &&
              platformOptions?.length !== undefined && (
                <DataProductCondition
                  handleChange={handleChange}
                  dataProductOptions={dataProductOptions}
                />
              )}
            {sensorModeOptions?.length !== 0 &&
              sensorModeOptions?.length !== undefined && (
                <SensorModeCondition
                  handleChange={handleChange}
                  sensorModeOptions={sensorModeOptions}
                />
              )}
            {(areTrishnaSectionsVisible || areCO3DSectionVisible) && (
              <>
                {((areTrishnaSectionsVisible && isUserAllowedToTrishnaMode) ||
                  (areCO3DSectionVisible && isUserAllowedToCO3DMode)) && (
                  <ModeCondition
                    handleChange={handleChange}
                    modeOptions={visibilityOptions}
                  />
                )}
                <AcquisitionTypeCondition
                  handleChange={handleChange}
                  acquisitionTypeOptions={acquisitionTypeOptions}
                />
                {areTrishnaSectionsVisible && (
                  <GenericSlider
                    handleChange={handleChange}
                    sliderSection={filterSections.waterCover}
                    sliderValues={sliderValues}
                    setSliderValues={setSliderValues}
                    sliderPosition={0}
                  />
                )}
              </>
            )}
            {isCloudCoverVisible && (
              <GenericSlider
                handleChange={handleChange}
                sliderSection={filterSections.cloudCover}
                sliderValues={sliderValues}
                setSliderValues={setSliderValues}
                sliderPosition={areTrishnaSectionsVisible ? 1 : 0}
              />
            )}
            {areTrishnaSectionsVisible && (
              <GenericSlider
                handleChange={handleChange}
                sliderSection={filterSections.noDataPixel}
                sliderValues={sliderValues}
                setSliderValues={setSliderValues}
                sliderPosition={2}
              />
            )}
          </div>
        )}
      </div>
    </ScrollPanel>
  );
};
